import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import GlobalButton from "../CustomButton";
import Label from "../Label";
import CustomSelect from "../customSelect";
import { getWorkerBySubservices } from "../../services/workerApiFn";
import { handleOpenSnackBar } from "../../utils";
import { getCompanyServiceDetail } from "../../services/comanyApiFn";
import TextInput from "../TextInputs";
import { CURRENCY } from "../../constants/Variables";
import AppLoader from "../AppLoader";
import { assignedAppoitment } from "../../services/appointmentApiFn";
import { t } from "i18next";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(2),
  },
  "& .MuiPaper-root": {
    width: "500px",
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
}));

const AppointmentAssigningDialog = forwardRef(({ availableCompanies }, ref) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dialogData, setDialogData] = useState({});
  const [companyService, setCompanyService] = useState(null);
  const [allWorkers, setAllWorkers] = useState([]);
  const [inputValues, setInputValues] = useState({
    assignedCompany: "",
    assignedWorker: "",
    workerStatus: "",
    appointmentStatus: "",
  });

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setDialogData(params);
      setInputValues((pre) => ({
        ...pre,
        assignedCompany: params?.data?.companyId?._id,
        assignedWorker: params?.data?.workerId?._id,
      }));
      handleFetchAllWorkers(
        params?.data?.companyId?._id,
        params?.data?.serviceSubCategoryId?._id
      );
      handleFetchCompanyService(
        params?.data?.companyId?._id,
        params?.data?.serviceSubCategoryId?._id
      );
      setOpen(true);
    },
    closeDialog() {
      handleClose();
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setDialogData({});
  };

  const handleFetchAllWorkers = async (companyId, serviceId) => {
    try {
      let payload = {
        companyId,
        subServices: [serviceId],
      };
      setIsLoading(true);
      const response = await getWorkerBySubservices(payload);
      if (response.data.status === "success") {
        const data = response.data.data;
        setAllWorkers(data);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFetchCompanyService = async (companyId, serviceId) => {
    try {
      setIsLoading(true);
      const response = await getCompanyServiceDetail(companyId, serviceId);
      if (response.data.status === "success") {
        const data = response.data.data;
        setCompanyService(data);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeInputs = (event) => {
    const { name, value } = event.target;
    if (name === "assignedCompany" && value) {
      handleFetchAllWorkers(value, dialogData?.data?.serviceSubCategoryId?._id);
      handleFetchCompanyService(
        value,
        dialogData?.data?.serviceSubCategoryId?._id
      );
      setInputValues((pre) => ({ ...pre, assignedWorker: "" }));
    }
    setInputValues((pre) => ({ ...pre, [name]: value }));
  };

  const renderPrice = () => {
    const type = companyService?.serviceSubCategoryId?.type;

    if (type === "area") {
      return {
        area: companyService?.price?.area,
      };
    }
    if (type === "fixed") {
      return {
        area: companyService?.price?.fixed,
      };
    }
    if (type === "room+area") {
      return {
        area: companyService?.price?.area,
        room: companyService?.price?.room,
      };
    }
    if (type === "interior+exterior") {
      return {
        interior: companyService?.price?.interior,
        exterior: companyService?.price?.exterior,
      };
    }
  };

  const handleUpdateAppointment = async () => {
    const selectedCompanay = availableCompanies?.find(
      (item) => item._id === inputValues?.assignedCompany
    );
    const selectedWorker = allWorkers?.find(
      (item) => item._id === inputValues?.assignedWorker
    );

    if (!selectedCompanay)
      return handleOpenSnackBar("error", t("pleaseSelectCompany"));
    if (!selectedWorker)
      return handleOpenSnackBar("error", t("pleaseSelectWorker"));

    if (
      dialogData?.data?.companyId?._id === inputValues?.assignedCompany &&
      dialogData?.data?.workerId?._id === inputValues?.assignedWorker
    ) {
      return handleOpenSnackBar("error", t("pleaseSelectDifferentWorker"));
    }

    let payload = {
      companyId: inputValues?.assignedCompany,
      workerId: inputValues?.assignedWorker,
      companyServiceId: companyService?._id,
      companyServiceDetails: companyService,
      companyDetails: selectedCompanay,
      workerDetails: selectedWorker,
      workerStatus: "pending",
      companyPercentage: companyService.percentage,
      start: dialogData?.data?.start,
      end: dialogData?.data?.end,
    };
    // return;
    try {
      setIsLoading(true);
      const response = await assignedAppoitment(dialogData?.data?._id, payload);
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response?.data?.message);
        handleClose();
        dialogData?.getData();
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
      // call api to update appointment
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="client-block-dialog-title"
        open={open}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="client-block-dialog-title">
          {t("updateAppointment")}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Label labelText={t("assignedCompany")} />
              <CustomSelect
                value={inputValues?.assignedCompany}
                name={"assignedCompany"}
                onChange={handleChangeInputs}
              >
                <MenuItem value={""}>{t("selectCompany")}</MenuItem>
                {availableCompanies?.map((item) => {
                  return (
                    <MenuItem key={item._id} value={item._id}>
                      {item?.name}
                    </MenuItem>
                  );
                })}
              </CustomSelect>
              {availableCompanies?.length === 0 && (
                <Typography fontSize={"0.75rem"} color={"error.main"}>
                  {t("noCompanyFound")}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Label labelText={t("assignedWorker")} />
              <CustomSelect
                value={inputValues?.assignedWorker}
                name={"assignedWorker"}
                onChange={handleChangeInputs}
              >
                <MenuItem value={""}>{t("selectWorker")}</MenuItem>
                {allWorkers?.map((item) => {
                  return (
                    <MenuItem key={item._id} value={item._id}>
                      {item?.name}
                    </MenuItem>
                  );
                })}
              </CustomSelect>
              {allWorkers?.length === 0 && (
                <Typography fontSize={"0.75rem"} color={"error.main"}>
                  {t("noWorkerFound")}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" fontWeight={600}>
                {t("serviceDetail")}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Label labelText={t("companyPercentage")} />
              <TextInput
                value={companyService?.percentage}
                InputEndIcon={"%"}
              />
            </Grid>

            {companyService?.serviceSubCategoryId?.type === "area" && (
              <Grid item xs={12} md={4}>
                <Label labelText={t("perAreaCharges")} />
                <TextInput
                  value={renderPrice()?.area}
                  InputEndIcon={CURRENCY}
                />
              </Grid>
            )}

            {companyService?.serviceSubCategoryId?.type === "fixed" && (
              <Grid item xs={12} md={4}>
                <Label labelText={t("servicesCharges")} />
                <TextInput
                  value={renderPrice()?.fixed}
                  InputEndIcon={CURRENCY}
                />
              </Grid>
            )}

            {companyService?.serviceSubCategoryId?.type === "room+area" && (
              <>
                <Grid item xs={12} md={4}>
                  <Label labelText={t("perRoomCharges")} />
                  <TextInput
                    value={renderPrice()?.room}
                    InputEndIcon={CURRENCY}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Label labelText={t("perAreaCharges")} />
                  <TextInput
                    value={renderPrice()?.area}
                    InputEndIcon={CURRENCY}
                  />
                </Grid>
              </>
            )}

            {companyService?.serviceSubCategoryId?.type ===
              "interior+exterior" && (
              <>
                <Grid item xs={12} md={4}>
                  <Label labelText={t("interiorCharges")} />
                  <TextInput
                    value={renderPrice()?.interior}
                    InputEndIcon={CURRENCY}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Label labelText={t("exteriorCharges")} />
                  <TextInput
                    value={renderPrice()?.exterior}
                    InputEndIcon={CURRENCY}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <GlobalButton
            variant="outlined"
            btnText="close"
            handleClickBtn={handleClose}
            styleProps={{
              height: 45,
            }}
          />
          <GlobalButton
            variant="contained"
            btnText="update"
            styleProps={{
              height: 45,
            }}
            handleClickBtn={handleUpdateAppointment}
          />
        </DialogActions>
      </BootstrapDialog>
    </>
  );
});

export default AppointmentAssigningDialog;
